// Conversion en JPEG, dans le navigateur, des formats que l'API refuse : HEIC/HEIF et WEBP.
//
// POURQUOI. Les Samsung récents photographient en HEIC, Chrome sur iPhone transmet les HEIC
// tels quels (parfois nommés `image.jpeg`), et certains Android exportent en WEBP : l'API
// n'accepte que JPEG et PNG, le membre voyait un aperçu gris puis un refus (415). Une dizaine de
// membres par jour (point du 12/10/2026). Décision de Rudy : convertir côté navigateur, l'API ne
// change rien à son traitement.
//
// LES MÉTADONNÉES SONT RECOPIÉES, TOUTES. Le décodage en pixels les perd ; on les extrait donc de
// l'original pour les réinjecter dans le JPEG produit : EXIF, XMP, profil de couleur (ICC) et
// signature d'origine C2PA. L'API les lit dès l'envoi temporaire (marqueurs « contenu IA »,
// services/aiContent/) ; c'est son traitement d'image qui les efface ensuite.
//   - Seule valeur modifiée : l'orientation EXIF, remise à « normal ». La conversion redresse déjà
//     les pixels ; laisser l'indicateur ferait tourner la photo une seconde fois à l'affichage.
//   - La signature C2PA est recopiée telle quelle : elle ne « colle » plus au fichier (l'image a
//     changé), mais l'API n'en vérifie que la présence.
//
// Le WEBP animé est refusé (même règle que le GIF) : une conversion n'en garderait qu'une image.
//
// La bibliothèque HEIC (libheif en WebAssembly, ~3 Mo, licence LGPL) n'est chargée que lorsqu'un
// HEIC est effectivement choisi.

export const IMAGE_CONVERT_ERROR = {
    ANIMATED: 'animated',
    DECODE_FAILED: 'decode_failed',
};

export class ImageConvertError extends Error {
    constructor(reason, cause = null) {
        super(`Conversion de l'image impossible (${reason})`);
        this.name = 'ImageConvertError';
        this.reason = reason;
        this.cause = cause;
    }
}

export const IMAGE_FORMAT = { HEIC: 'heic', WEBP: 'webp' };

const JPEG_QUALITY = 0.92;
const HEIF_BRANDS = ['heic', 'heix', 'hevc', 'hevx', 'heim', 'heis', 'hevm', 'hevs'];
// Marques génériques du conteneur HEIF, sans codec désigné. Une photo retouchée puis réenregistrée
// par la galerie Samsung (`…heic~2.heif`) ne porte que celles-ci : refusée en 415 le 13/10/2026 faute
// d'être reconnue. L'AVIF les porte aussi : il est écarté par ses propres marques.
const HEIF_GENERIC_BRANDS = ['mif1', 'msf1', 'miaf'];
const AVIF_BRANDS = ['avif', 'avis'];
const HEIF_TYPES = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];
// Identifiant des boîtes `uuid` qui portent un manifeste C2PA dans un fichier ISO-BMFF (HEIF).
const C2PA_BMFF_UUID = 'd8fec3d61b0e483c92975828877ec481';
// Charge utile maximale d'un segment JPEG (la longueur, sur 2 octets, se compte elle-même).
const MAX_SEGMENT_PAYLOAD = 65533;

const ascii = (bytes, start, length) => String.fromCharCode(...bytes.subarray(start, start + length));
const hex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
const encoder = new TextEncoder();

/* -------------------------------------------------------------------------- */
/* Détection                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Format réel, lu dans les premiers octets — le type annoncé ment parfois (HEIC nommé `.jpeg`).
 * @param {Uint8Array} head au moins 32 octets
 * @param {string} [declaredType] type annoncé par le navigateur, simple indice
 * @returns {{format: ?string, animated: boolean}}
 */
export const sniffImageFormat = (head, declaredType = '') => {
    if (head.length >= 12 && ascii(head, 4, 4) === 'ftyp') {
        const boxSize = Math.min(new DataView(head.buffer, head.byteOffset).getUint32(0), head.length);
        const brands = [ascii(head, 8, 4)];
        for (let i = 16; i + 4 <= boxSize; i += 4) brands.push(ascii(head, i, 4));
        const isAvif = brands.some((b) => AVIF_BRANDS.includes(b));
        const isHeif = brands.some((b) => HEIF_BRANDS.includes(b))
            || (!isAvif && (brands.some((b) => HEIF_GENERIC_BRANDS.includes(b)) || HEIF_TYPES.includes(declaredType)));
        if (isHeif) return { format: IMAGE_FORMAT.HEIC, animated: false };
    }
    if (head.length >= 21 && ascii(head, 0, 4) === 'RIFF' && ascii(head, 8, 4) === 'WEBP') {
        // Chunk étendu VP8X : le bit 0x02 de ses drapeaux signale une animation.
        const animated = ascii(head, 12, 4) === 'VP8X' && (head[20] & 0x02) !== 0;
        return { format: IMAGE_FORMAT.WEBP, animated };
    }
    return { format: null, animated: false };
};

/**
 * @param {File} file
 * @returns {Promise<{format: ?string, animated: boolean}>} format nul si rien à convertir
 */
export const detectConvertibleImage = async (file) => {
    // Type vide accepté : sous Windows, un HEIC arrive souvent sans type MIME.
    if (!file || (file.type && !file.type.startsWith('image/'))) return { format: null, animated: false };
    const head = new Uint8Array(await file.slice(0, 64).arrayBuffer());
    return sniffImageFormat(head, file.type);
};

/* -------------------------------------------------------------------------- */
/* Extraction des métadonnées                                                  */
/* -------------------------------------------------------------------------- */

const readBoxes = (bytes, start, end) => {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const boxes = [];
    let offset = start;
    while (offset + 8 <= end) {
        let size = view.getUint32(offset);
        const type = ascii(bytes, offset + 4, 4);
        let header = 8;
        if (size === 1) {
            size = Number(view.getBigUint64(offset + 8));
            header = 16;
        } else if (size === 0) {
            size = end - offset;
        }
        if (size < header || offset + size > end) break;
        boxes.push({ type, start: offset, dataStart: offset + header, end: offset + size });
        offset += size;
    }
    return boxes;
};

const readUint = (view, offset, size) => {
    if (size === 0) return 0;
    if (size === 2) return view.getUint16(offset);
    if (size === 4) return view.getUint32(offset);
    if (size === 8) return Number(view.getBigUint64(offset));
    return 0;
};

const readCString = (bytes, offset) => {
    let end = offset;
    while (end < bytes.length && bytes[end] !== 0) end += 1;
    return { value: ascii(bytes, offset, end - offset), next: end + 1 };
};

// Le manifeste C2PA est une « superboîte » JUMBF (`jumb`) : on la repère dans la charge utile.
const findJumbf = (bytes, start, end) => {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    for (let i = start + 4; i + 4 <= end; i += 1) {
        if (bytes[i] === 0x6a && ascii(bytes, i, 4) === 'jumb') {
            const length = view.getUint32(i - 4);
            if (length >= 8 && i - 4 + length <= end) return bytes.slice(i - 4, i - 4 + length);
        }
    }
    return null;
};

// TIFF (EXIF) : débute par « II*\0 » ou « MM\0* ».
const isTiff = (bytes, offset) => (bytes[offset] === 0x49 && bytes[offset + 1] === 0x49 && bytes[offset + 2] === 0x2a)
    || (bytes[offset] === 0x4d && bytes[offset + 1] === 0x4d && bytes[offset + 3] === 0x2a);

const tiffFrom = (bytes) => {
    for (let i = 0; i + 4 <= Math.min(bytes.length, 64); i += 1) {
        if (isTiff(bytes, i)) return bytes.slice(i);
    }
    return null;
};

/**
 * HEIF : métadonnées rangées comme des « items » (Exif, XMP en `mime`) localisés par `iloc`,
 * profil ICC dans la propriété `colr`, manifeste C2PA dans une boîte `uuid` de premier niveau.
 * @param {Uint8Array} bytes
 */
const extractHeifMetadata = (bytes) => {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const found = {};
    const top = readBoxes(bytes, 0, bytes.length);

    const uuidBox = top.find((b) => b.type === 'uuid' && hex(bytes.subarray(b.dataStart, b.dataStart + 16)) === C2PA_BMFF_UUID);
    if (uuidBox) found.c2pa = findJumbf(bytes, uuidBox.dataStart + 16, uuidBox.end);

    const meta = top.find((b) => b.type === 'meta');
    if (!meta) return found;
    // `meta` est une « full box » : 4 octets de version et drapeaux avant ses enfants.
    const children = readBoxes(bytes, meta.dataStart + 4, meta.end);

    const items = {};
    const iinf = children.find((b) => b.type === 'iinf');
    if (iinf) {
        const version = bytes[iinf.dataStart];
        const entriesStart = iinf.dataStart + 4 + (version === 0 ? 2 : 4);
        for (const infe of readBoxes(bytes, entriesStart, iinf.end).filter((b) => b.type === 'infe')) {
            const v = bytes[infe.dataStart];
            if (v < 2) continue;
            let o = infe.dataStart + 4;
            const id = v === 2 ? view.getUint16(o) : view.getUint32(o);
            o += (v === 2 ? 2 : 4) + 2;
            const itemType = ascii(bytes, o, 4);
            o += 4;
            const name = readCString(bytes, o);
            const contentType = itemType === 'mime' ? readCString(bytes, name.next).value : null;
            items[id] = { type: itemType, contentType };
        }
    }

    const idat = children.find((b) => b.type === 'idat');
    const iloc = children.find((b) => b.type === 'iloc');
    const locate = {};
    if (iloc) {
        const version = bytes[iloc.dataStart];
        let o = iloc.dataStart + 4;
        const offsetSize = bytes[o] >> 4;
        const lengthSize = bytes[o] & 0x0f;
        const baseOffsetSize = bytes[o + 1] >> 4;
        const indexSize = version >= 1 ? bytes[o + 1] & 0x0f : 0;
        o += 2;
        const count = version < 2 ? view.getUint16(o) : view.getUint32(o);
        o += version < 2 ? 2 : 4;
        for (let i = 0; i < count; i += 1) {
            const id = version < 2 ? view.getUint16(o) : view.getUint32(o);
            o += version < 2 ? 2 : 4;
            const method = version >= 1 ? view.getUint16(o) & 0x0f : 0;
            if (version >= 1) o += 2;
            o += 2; // data_reference_index
            const base = readUint(view, o, baseOffsetSize);
            o += baseOffsetSize;
            const extentCount = view.getUint16(o);
            o += 2;
            const extents = [];
            for (let e = 0; e < extentCount; e += 1) {
                o += indexSize;
                const extentOffset = readUint(view, o, offsetSize);
                o += offsetSize;
                const extentLength = readUint(view, o, lengthSize);
                o += lengthSize;
                extents.push([base + extentOffset, extentLength]);
            }
            locate[id] = { method, extents };
        }
    }

    const itemBytes = (id) => {
        const loc = locate[id];
        if (!loc || loc.method === 2) return null;
        const origin = loc.method === 1 ? (idat ? idat.dataStart : null) : 0;
        if (origin === null) return null;
        const parts = loc.extents.map(([offset, length]) => bytes.subarray(origin + offset, origin + offset + length));
        const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
        let k = 0;
        parts.forEach((p) => { out.set(p, k); k += p.length; });
        return out;
    };

    for (const [id, item] of Object.entries(items)) {
        if (item.type === 'Exif' && !found.exif) {
            const data = itemBytes(Number(id));
            // Charge utile Exif d'un HEIF : 4 octets donnant le décalage de l'en-tête TIFF.
            if (data && data.length > 8) found.exif = tiffFrom(data.subarray(4 + new DataView(data.buffer, data.byteOffset).getUint32(0))) || tiffFrom(data);
        }
        if (item.type === 'mime' && /rdf\+xml/i.test(item.contentType || '') && !found.xmp) {
            found.xmp = itemBytes(Number(id));
        }
    }

    const iprp = children.find((b) => b.type === 'iprp');
    const ipco = iprp && readBoxes(bytes, iprp.dataStart, iprp.end).find((b) => b.type === 'ipco');
    if (ipco) {
        for (const colr of readBoxes(bytes, ipco.dataStart, ipco.end).filter((b) => b.type === 'colr')) {
            const colourType = ascii(bytes, colr.dataStart, 4);
            if (colourType === 'prof' || colourType === 'rICC') {
                found.icc = bytes.slice(colr.dataStart + 4, colr.end);
                break;
            }
        }
    }
    return found;
};

/**
 * WEBP : métadonnées dans des chunks RIFF nommés (`EXIF`, `XMP `, `ICCP`, `C2PA`).
 * @param {Uint8Array} bytes
 */
const extractWebpMetadata = (bytes) => {
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const found = {};
    let o = 12;
    while (o + 8 <= bytes.length) {
        const id = ascii(bytes, o, 4);
        const size = view.getUint32(o + 4, true);
        const data = bytes.subarray(o + 8, Math.min(o + 8 + size, bytes.length));
        if (id === 'EXIF') found.exif = tiffFrom(data);
        else if (id === 'XMP ') found.xmp = data.slice();
        else if (id === 'ICCP') found.icc = data.slice();
        else if (id === 'C2PA') found.c2pa = findJumbf(bytes, o + 8, o + 8 + size) || data.slice();
        o += 8 + size + (size % 2);
    }
    return found;
};

/* -------------------------------------------------------------------------- */
/* Réinjection dans le JPEG                                                    */
/* -------------------------------------------------------------------------- */

// Orientation EXIF (balise 0x0112, IFD0) remise à 1 : les pixels sont déjà redressés.
const resetOrientation = (tiff) => {
    const out = tiff.slice();
    try {
        const view = new DataView(out.buffer);
        const little = out[0] === 0x49;
        const ifd0 = view.getUint32(4, little);
        const count = view.getUint16(ifd0, little);
        for (let i = 0; i < count; i += 1) {
            const entry = ifd0 + 2 + i * 12;
            if (view.getUint16(entry, little) === 0x0112) {
                view.setUint16(entry + 8, 1, little);
                break;
            }
        }
    } catch (e) {
        // EXIF mal formé : recopié tel quel plutôt que perdu.
    }
    return out;
};

const segment = (marker, ...parts) => {
    const length = parts.reduce((n, p) => n + p.length, 0) + 2;
    const out = new Uint8Array(length + 2);
    out[0] = 0xff;
    out[1] = marker;
    out[2] = length >> 8;
    out[3] = length & 0xff;
    let k = 4;
    parts.forEach((p) => { out.set(p, k); k += p.length; });
    return out;
};

const u16 = (n) => new Uint8Array([n >> 8, n & 0xff]);
const u32 = (n) => new Uint8Array([(n >>> 24) & 0xff, (n >>> 16) & 0xff, (n >>> 8) & 0xff, n & 0xff]);

/**
 * Segments JPEG portant les métadonnées, et le compte rendu de ce qui a pu être recopié.
 * @param {{exif?: Uint8Array, xmp?: Uint8Array, icc?: Uint8Array, c2pa?: Uint8Array}} meta
 */
const buildSegments = (meta) => {
    const segments = [];
    const copied = [];
    const skipped = [];

    if (meta.exif) {
        const header = encoder.encode('Exif\0\0');
        if (meta.exif.length + header.length <= MAX_SEGMENT_PAYLOAD) {
            segments.push(segment(0xe1, header, resetOrientation(meta.exif)));
            copied.push('exif');
        } else skipped.push('exif (trop volumineux pour un segment JPEG)');
    }
    if (meta.xmp) {
        const header = encoder.encode('http://ns.adobe.com/xap/1.0/\0');
        if (meta.xmp.length + header.length <= MAX_SEGMENT_PAYLOAD) {
            segments.push(segment(0xe1, header, meta.xmp));
            copied.push('xmp');
        } else skipped.push('xmp (trop volumineux pour un segment JPEG)');
    }
    if (meta.icc) {
        // Profil découpé en morceaux numérotés (« ICC_PROFILE », n° du morceau, nombre total).
        const header = encoder.encode('ICC_PROFILE\0');
        const chunk = MAX_SEGMENT_PAYLOAD - header.length - 2;
        const total = Math.ceil(meta.icc.length / chunk);
        if (total <= 255) {
            for (let i = 0; i < total; i += 1) {
                segments.push(segment(0xe2, header, new Uint8Array([i + 1, total]), meta.icc.subarray(i * chunk, (i + 1) * chunk)));
            }
            copied.push('icc');
        } else skipped.push('icc (trop volumineux)');
    }
    if (meta.c2pa && meta.c2pa.length > 8) {
        // JUMBF dans un JPEG (segments APP11) : « JP », n° d'instance, n° de paquet, puis l'en-tête
        // de la superboîte répété dans chaque segment, suivi de la suite du contenu.
        const boxHeader = meta.c2pa.subarray(0, 8);
        const content = meta.c2pa.subarray(8);
        const chunk = MAX_SEGMENT_PAYLOAD - 2 - 2 - 4 - boxHeader.length;
        const total = Math.max(1, Math.ceil(content.length / chunk));
        for (let i = 0; i < total; i += 1) {
            segments.push(segment(0xeb, encoder.encode('JP'), u16(1), u32(i + 1), boxHeader, content.subarray(i * chunk, (i + 1) * chunk)));
        }
        copied.push('c2pa');
    }
    return { segments, copied, skipped };
};

// Retire le profil de couleur que le navigateur ajoute à son JPEG (sRGB). Quand on recopie celui
// de l'original, garder les deux laisserait les lecteurs choisir au hasard.
const stripIccSegments = (jpeg) => {
    const parts = [jpeg.subarray(0, 2)];
    let o = 2;
    while (o + 4 <= jpeg.length && jpeg[o] === 0xff && jpeg[o + 1] !== 0xda) {
        const length = 2 + ((jpeg[o + 2] << 8) | jpeg[o + 3]);
        const isIcc = jpeg[o + 1] === 0xe2 && ascii(jpeg, o + 4, 11) === 'ICC_PROFILE';
        if (!isIcc) parts.push(jpeg.subarray(o, o + length));
        o += length;
    }
    parts.push(jpeg.subarray(o));
    const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
    let k = 0;
    parts.forEach((p) => { out.set(p, k); k += p.length; });
    return out;
};

// Insère les segments juste après SOI (et après l'APP0 JFIF du navigateur, s'il y en a un).
const injectSegments = (jpeg, segments) => {
    if (!segments.length) return jpeg;
    let at = 2;
    if (jpeg[2] === 0xff && jpeg[3] === 0xe0) at = 4 + ((jpeg[4] << 8) | jpeg[5]);
    const length = segments.reduce((n, s) => n + s.length, 0);
    const out = new Uint8Array(jpeg.length + length);
    out.set(jpeg.subarray(0, at), 0);
    let k = at;
    segments.forEach((s) => { out.set(s, k); k += s.length; });
    out.set(jpeg.subarray(at), k);
    return out;
};

/* -------------------------------------------------------------------------- */
/* Conversion                                                                  */
/* -------------------------------------------------------------------------- */

const canvasToJpeg = (canvas) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('toBlob a rendu un résultat vide'))), 'image/jpeg', JPEG_QUALITY);
});

// WEBP : décodé par le navigateur lui-même. Fond blanc sous la transparence (le JPEG n'en a pas).
// Sans conversion de couleurs : les pixels restent dans l'espace de l'original, que décrit le
// profil ICC recopié (comme pour le HEIC, que libheif décode sans le convertir).
const decodeWebp = async (file) => {
    const bitmap = await createImageBitmap(file, { colorSpaceConversion: 'none' });
    const canvas = document.createElement('canvas');
    canvas.width = bitmap.width;
    canvas.height = bitmap.height;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(bitmap, 0, 0);
    bitmap.close?.();
    return canvasToJpeg(canvas);
};

const decodeHeic = async (file) => {
    const { heicTo } = await import(/* webpackChunkName: "heic-to" */ 'heic-to');
    return heicTo({ blob: file, type: 'image/jpeg', quality: JPEG_QUALITY });
};

const jpegName = (name = 'photo') => `${name.replace(/\.[^./\\]+$/, '') || 'photo'}.jpg`;

/**
 * @param {File} file
 * @param {string} format IMAGE_FORMAT.*
 * @returns {Promise<File>} un JPEG portant `__conversion` (compte rendu, journalisé par l'API)
 * @throws {ImageConvertError}
 */
export const convertToJpeg = async (file, format) => {
    const startedAt = performance.now();
    let original;
    let decoded;
    try {
        original = new Uint8Array(await file.arrayBuffer());
        decoded = format === IMAGE_FORMAT.HEIC ? await decodeHeic(file) : await decodeWebp(file);
    } catch (error) {
        throw new ImageConvertError(IMAGE_CONVERT_ERROR.DECODE_FAILED, error);
    }

    // Les métadonnées ne doivent jamais faire échouer la conversion : au pire, le JPEG part sans.
    let meta = {};
    let metaError = null;
    try {
        meta = format === IMAGE_FORMAT.HEIC ? extractHeifMetadata(original) : extractWebpMetadata(original);
    } catch (error) {
        metaError = error?.message || String(error);
    }
    const { segments, copied, skipped } = buildSegments(meta);
    let decodedBytes = new Uint8Array(await decoded.arrayBuffer());
    if (copied.includes('icc')) decodedBytes = stripIccSegments(decodedBytes);
    const jpegBytes = injectSegments(decodedBytes, segments);

    const converted = new File([jpegBytes], jpegName(file.name), { type: 'image/jpeg', lastModified: file.lastModified });
    let width = null;
    let height = null;
    try {
        const bitmap = await createImageBitmap(converted);
        width = bitmap.width;
        height = bitmap.height;
        bitmap.close?.();
    } catch (e) {
        // Dimensions seulement informatives.
    }

    // Déjà en mémoire : rien à recopier en cas de refus de l'envoi (cf. useUploader).
    converted.__isLocalCopy = true;
    converted.__copiedFrom = file;
    converted.__conversion = {
        original: {
            nom: file.name,
            type: file.type || null,
            taille: file.size,
            format,
            metadonnees: Object.fromEntries(Object.entries(meta).filter(([, v]) => v).map(([k, v]) => [k, v.length])),
        },
        converti: { nom: converted.name, taille: converted.size, largeur: width, hauteur: height },
        metadonneesRecopiees: copied,
        metadonneesIgnorees: skipped,
        erreurMetadonnees: metaError,
        dureeMs: Math.round(performance.now() - startedAt),
    };
    return converted;
};
